/**
 * ModelPlannerWizardModal Component
 * Main modal for the Model Planner Wizard
 * Manages state, navigation, and coordinates all sub-components
 * Updated: Resume partially completed sections functionality
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { X } from "lucide-react";
import WizardNavigation from "./WizardNavigation";
import WizardQuestion from "./WizardQuestion";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import ExportButton from "./ExportButton";
import WizardEngine from "../../utils/wizardEngine";
import ModelPlannerService from "../../services/modelPlannerService";
import PlannerConfigService from "../../services/plannerConfigService";
import modelPlannerExportService from "../../services/modelPlannerExportService";
import { formatJobRevNumber } from '../../utils/projectFormatting';
import "./ModelPlannerWizardModal.css";
import { useTrackModal } from "../../hooks/useTrackModal";

const ModelPlannerWizardModal = ({
  project,
  isOpen,
  onClose,
  onOpenDiscrepancyModal,
}) => {
  useTrackModal("ModelPlannerWizardModal", isOpen);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [sessionData, setSessionData] = useState({
    ponds: [],
    curbTypes: [],
    gradingSections: null,
    walls: [],
    ditchesSwales: null,
    sidewalks: null,
    adaRamps: [],
    buildingPads: null,
    grateElevations: null,
  });

  // Initialize from URL params if available
  const urlParams = new URLSearchParams(window.location.search);
  const [currentSection, setCurrentSection] = useState(urlParams.get('section') || "ponds");
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [loopState, setLoopState] = useState(null);

  // Update URL when section/question changes
  useEffect(() => {
    if (!isOpen) return;

    const params = new URLSearchParams(window.location.search);
    params.set('section', currentSection);
    if (currentQuestion?.question_id) {
      params.set('question', currentQuestion.question_id);
    } else {
      params.delete('question');
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [currentSection, currentQuestion, isOpen]);
  const [currentItemData, setCurrentItemData] = useState({});
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [singleQuestionEditMode, setSingleQuestionEditMode] = useState(null); // { active: true, sectionKey: 'building-pads', targetQuestionId: 'commercial_plan_form' }
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    itemId: null,
    itemName: "",
    itemType: "",
    sectionKey: "",
    cascadeInfo: null,
  });

  const [wizardEngine, setWizardEngine] = useState(null);
  const autoSaveTimeout = useRef(null);
  const questionAreaRef = useRef(null);
  const currentItemDataRef = useRef(currentItemData); // Track latest data for edit mode checks

  // Keep ref in sync with state
  useEffect(() => {
    currentItemDataRef.current = currentItemData;
  }, [currentItemData]);

  // Load configuration and session data on mount
  useEffect(() => {
    if (isOpen && project?.id) {
      loadConfigAndSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, project?.id]);

  /**
   * Load configuration from database and initialize wizard engine
   */
  const loadConfigAndSession = async () => {
    setLoading(true);
    try {
      // Load configuration from database
      console.log(
        "[ModelPlannerWizardModal] Loading configuration from database...",
      );
      const config = await PlannerConfigService.loadConfiguration();
      console.log("[ModelPlannerWizardModal] Configuration loaded:", config);

      // Initialize WizardEngine with loaded config
      const engine = new WizardEngine(config.sections);
      setWizardEngine(engine);

      // Now load session data
      await loadSession();
    } catch (error) {
      console.error(
        "[ModelPlannerWizardModal] Error loading configuration:",
        error,
      );
      // Fall back to creating WizardEngine without config (will use JSON)
      console.warn(
        "[ModelPlannerWizardModal] Falling back to JSON configuration",
      );
      const fallbackEngine = new WizardEngine();
      setWizardEngine(fallbackEngine);
      await loadSession();
    }
  };

  // Browser history integration - update URL when wizard state changes
  useEffect(() => {
    if (!isOpen || loading) return;

    // Build URL params based on current state
    const params = new URLSearchParams(window.location.search);

    if (currentSection) {
      params.set("mpSection", currentSection);
    }

    if (currentQuestion?.id) {
      params.set("mpQuestion", currentQuestion.id);
    }

    if (loopState?.currentIndex !== undefined) {
      params.set("mpIndex", loopState.currentIndex.toString());
    } else {
      params.delete("mpIndex");
    }

    // Update URL without causing page reload
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState(
      {
        modelPlanner: true,
        section: currentSection,
        questionId: currentQuestion?.id,
        loopIndex: loopState?.currentIndex,
      },
      "",
      newUrl,
    );
  }, [
    isOpen,
    loading,
    currentSection,
    currentQuestion?.id,
    loopState?.currentIndex,
  ]);

  // Listen for browser back/forward button
  useEffect(() => {
    if (!isOpen) return;

    const handlePopState = (event) => {
      // Only handle if this is a model planner state
      if (!event.state?.modelPlanner) {
        // User went back before wizard was opened, close the modal
        onClose();
        return;
      }

      // Restore wizard state from history
      const { section, questionId, loopIndex } = event.state;

      if (section && section !== currentSection) {
        handleSectionSelect(section);
      } else if (questionId && questionId !== currentQuestion?.id) {
        // Navigate to specific question
        const question = wizardEngine.getQuestion(section, questionId);
        if (question) {
          setCurrentQuestion(question);

          // Restore loop state if needed
          if (loopIndex !== undefined) {
            const sectionConfig = wizardEngine.getSection(section);
            if (sectionConfig?.type === "looped") {
              const dataKey = getSectionDataKey(section);
              const items = sessionData[dataKey] || [];
              const itemIds = items.map((i) => i.id);

              setLoopState({
                totalCount: items.length,
                currentIndex: loopIndex,
                itemIds: itemIds,
                loopStartQuestion: wizardEngine.getLoopStartQuestion(section),
                isEditMode: true,
                returnToSummary: true,
              });
            }
          }
        }
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentSection, currentQuestion?.id, sessionData, onClose]);

  // Watch for sessionData changes and ensure completed sections show summary
  useEffect(() => {
    console.log("[useEffect] Running with:", {
      loading,
      currentSection,
      currentQuestionType: currentQuestion?.type,
      currentQuestionId: currentQuestion?.id,
      singleQuestionEditMode,
    });

    // Skip if still loading or no session data yet
    if (loading || !currentSection || !session || !sessionData) {
      console.log("[useEffect] Skipping - loading or missing data");
      return;
    }

    // Skip if we're already showing a summary
    if (currentQuestion?.type === "section_summary") {
      console.log("[useEffect] Skipping - already showing summary");
      return;
    }

    // Skip if we're in single question edit mode
    if (singleQuestionEditMode && singleQuestionEditMode.active) {
      console.log("[useEffect] Skipping - single question edit mode is active");
      return;
    }

    // Skip if we're showing the "add items" count question
    if (currentQuestion?.id === "add_items_count") {
      console.log("[useEffect] Skipping - showing add items count question");
      return;
    }

    // Skip if we're showing a confirmation page
    if (currentQuestion?.type === "confirmation") {
      console.log("[useEffect] Skipping - showing confirmation page");
      return;
    }

    // Check if current section is completed
    const dataKey = getSectionDataKey(currentSection);
    const sectionData = sessionData[dataKey];
    const isCompleted = checkSectionCompletion(currentSection, sectionData);

    console.log("[useEffect] Section completed?", isCompleted);

    // If section is completed but we're not showing summary, show it now
    if (isCompleted) {
      console.log(
        `[useEffect] Section ${currentSection} is completed, showing summary view`,
      );
      const sectionConfig = wizardEngine.getSection(currentSection);
      const summaryQuestion = {
        id: "section_summary",
        type: "section_summary",
        text: `${sectionConfig.label} Summary`,
        sectionKey: currentSection,
      };
      setCurrentQuestion(summaryQuestion);
      setCurrentItemData({});
      setAnsweredQuestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessionData,
    currentSection,
    loading,
    currentQuestion,
    session,
    singleQuestionEditMode,
  ]);

  // Helper function to check if a section is completed
  const checkSectionCompletion = (sectionKey, sectionData) => {
    const sectionConfig = wizardEngine.getSection(sectionKey);

    if (sectionConfig?.type === "single") {
      return sectionData?.completed === true;
    } else if (sectionConfig?.type === "looped") {
      const items = Array.isArray(sectionData) ? sectionData : [];
      return items.length > 0 && items.every((item) => item.completed === true);
    }

    return false;
  };

  const getSectionDataKey = (sectionKey) => {
    const keyMap = {
      ponds: "ponds",
      curb: "curbTypes",
      grading: "gradingSections",
      walls: "walls",
      ditches: "ditchesSwales",
      sidewalks: "sidewalks",
      "ada-ramps": "adaRamps",
      "building-pads": "buildingPads",
      "grate-elevations": "grateElevations",
    };
    return keyMap[sectionKey] || sectionKey;
  };

  const loadSession = async () => {
    setLoading(true);
    try {
      const { data, error } = await ModelPlannerService.getCompleteSession(
        project.id,
      );

      if (error) {
        console.error("Error loading session:", error);
        return;
      }

      if (data) {
        setSession(data.session);
        setSessionData({
          ponds: data.ponds || [],
          curbTypes: data.curbTypes || [],
          gradingSections: data.gradingSections,
          walls: data.walls || [],
          ditchesSwales: data.ditchesSwales,
          sidewalks: data.sidewalks,
          adaRamps: data.adaRamps || [],
          buildingPads: data.buildingPads,
          grateElevations: data.grateElevations,
        });

        // Set current section from session or default to first
        const startSection = data.session.current_section || "ponds";
        setCurrentSection(startSection);

        // Determine what to show based on section completion status
        const sectionConfig = wizardEngine.getSection(startSection);
        const dataKeyMap = {
          ponds: "ponds",
          curb: "curbTypes",
          grading: "gradingSections",
          walls: "walls",
          ditches: "ditchesSwales",
          sidewalks: "sidewalks",
          "ada-ramps": "adaRamps",
          "building-pads": "buildingPads",
          "grate-elevations": "grateElevations",
        };
        const dataKey = dataKeyMap[startSection] || startSection;
        const sessionDataForSection = {
          ponds: data.ponds || [],
          curbTypes: data.curbTypes || [],
          gradingSections: data.gradingSections,
          walls: data.walls || [],
          ditchesSwales: data.ditchesSwales,
          sidewalks: data.sidewalks,
          adaRamps: data.adaRamps || [],
          buildingPads: data.buildingPads,
          grateElevations: data.grateElevations,
        };
        const sectionData = sessionDataForSection[dataKey];

        let shouldShowSummary = false;

        if (sectionConfig && sectionConfig.type === "single") {
          // Single section - check if completed
          shouldShowSummary = sectionData?.completed === true;
        } else if (sectionConfig && sectionConfig.type === "looped") {
          // Looped section - check if all items are completed
          const items = Array.isArray(sectionData) ? sectionData : [];
          shouldShowSummary =
            items.length > 0 && items.every((item) => item.completed === true);
        }

        if (shouldShowSummary) {
          // Show summary view for completed section
          const summaryQuestion = {
            id: "section_summary",
            type: "section_summary",
            text: `${sectionConfig.label} Summary`,
            sectionKey: startSection,
          };
          setCurrentQuestion(summaryQuestion);
        } else {
          // Show initial question for incomplete section
          const initialQ = wizardEngine.getInitialQuestion(startSection);
          setCurrentQuestion(initialQ);
        }
      }
    } catch (error) {
      console.error("Error loading session:", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshSessionData = async () => {
    try {
      console.log("[refreshSessionData] Fetching complete session...");
      const { data, error } = await ModelPlannerService.getCompleteSession(
        project.id,
      );

      if (error) {
        console.error("Error refreshing session data:", error);
        return;
      }

      if (data) {
        console.log("[refreshSessionData] Received data from database:", data);
        console.log(
          "[refreshSessionData] gradingSections completed:",
          data.gradingSections?.completed,
        );
        console.log(
          "[refreshSessionData] ditchesSwales completed:",
          data.ditchesSwales?.completed,
        );
        console.log(
          "[refreshSessionData] sidewalks completed:",
          data.sidewalks?.completed,
        );

        // Only update sessionData, don't touch other state
        setSessionData({
          ponds: data.ponds || [],
          curbTypes: data.curbTypes || [],
          gradingSections: data.gradingSections,
          walls: data.walls || [],
          ditchesSwales: data.ditchesSwales,
          sidewalks: data.sidewalks,
          adaRamps: data.adaRamps || [],
          buildingPads: data.buildingPads,
          grateElevations: data.grateElevations,
        });
        console.log("[refreshSessionData] State updated");
      }
    } catch (error) {
      console.error("Error refreshing session data:", error);
    }
  };

  const autoSave = useCallback(
    async (updates) => {
      // Debounce auto-save
      if (autoSaveTimeout.current) {
        clearTimeout(autoSaveTimeout.current);
      }

      autoSaveTimeout.current = setTimeout(async () => {
        try {
          // Save based on section type
          await saveCurrentData(updates);
        } catch (error) {
          console.error("Auto-save error:", error);
        }
      }, 500);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, currentSection, loopState],
  );

  const saveCurrentData = async (updates) => {
    if (!session) return;

    const sectionKey = currentSection;
    const sectionConfig = wizardEngine.getSection(sectionKey);

    // Check if we're in an ADA ramp loop (nested within sidewalks)
    if (loopState && loopState.isADALoop) {
      // Handle ADA ramp loop separately
      const currentIndex = loopState.currentIndex;
      const existingItemId = loopState.itemIds[currentIndex];

      if (existingItemId) {
        // Update existing ADA ramp
        await updateLoopedItem("ada-ramps", existingItemId, updates);
      } else {
        // Create new ADA ramp
        const newItemId = await createLoopedItem(
          "ada-ramps",
          currentIndex,
          updates,
        );
        if (newItemId) {
          // Store item ID in loop state
          const updatedItemIds = [...loopState.itemIds];
          updatedItemIds[currentIndex] = newItemId;
          setLoopState((prev) => ({
            ...prev,
            itemIds: updatedItemIds,
          }));
        }
      }
    } else if (sectionConfig.type === "looped") {
      // Regular looped section - save individual item
      if (loopState) {
        const currentIndex = loopState.currentIndex;
        const existingItemId = loopState.itemIds[currentIndex];

        if (existingItemId) {
          // Update existing item
          await updateLoopedItem(sectionKey, existingItemId, updates);
        } else {
          // Create new item
          const newItemId = await createLoopedItem(
            sectionKey,
            currentIndex,
            updates,
          );
          if (newItemId) {
            // Store item ID in loop state
            const updatedItemIds = [...loopState.itemIds];
            updatedItemIds[currentIndex] = newItemId;
            setLoopState((prev) => ({
              ...prev,
              itemIds: updatedItemIds,
            }));
          }
        }
      }
    } else {
      // Single section - upsert
      await upsertSingleSection(sectionKey, updates);
    }

    // Update session's current_section
    await ModelPlannerService.updateSession(session.id, {
      current_section: sectionKey,
    });
  };

  const createAllLoopItems = async (sectionKey, totalCount) => {
    console.log("[Modal:createAllLoopItems] ===== CREATING LOOP ITEMS =====");
    console.log("[Modal:createAllLoopItems] sectionKey:", sectionKey);
    console.log("[Modal:createAllLoopItems] totalCount:", totalCount);
    console.log("[Modal:createAllLoopItems] session.id:", session?.id);

    const itemIds = [];

    for (let i = 0; i < totalCount; i++) {
      console.log(
        `[Modal:createAllLoopItems] Creating item ${i + 1} of ${totalCount} for section: ${sectionKey}`,
      );
      const itemId = await createLoopedItem(sectionKey, i, {});
      console.log(
        `[Modal:createAllLoopItems] Item ${i + 1} created with ID:`,
        itemId,
      );

      if (itemId) {
        itemIds.push(itemId);
      } else {
        console.error(
          `[Modal:createAllLoopItems] FAILED to create item ${i + 1} - returned null/undefined`,
        );
      }
    }

    console.log("[Modal:createAllLoopItems] Final itemIds array:", itemIds);
    console.log(
      "[Modal:createAllLoopItems] Successfully created",
      itemIds.length,
      "out of",
      totalCount,
      "items",
    );
    return itemIds;
  };

  const createLoopedItem = async (sectionKey, order, data) => {
    console.log("[Modal:createLoopedItem] Creating item:", { sectionKey, order, data });

    const { data: created, error } = await ModelPlannerService.createSectionData(
      session.id,
      sectionKey,
      data,
      order
    );

    if (error) {
      console.error("[Modal:createLoopedItem] Error creating item:", error);
      return null;
    }

    console.log("[Modal:createLoopedItem] Item created:", created?.id);
    return created?.id || null;
  };

  const updateLoopedItem = async (sectionKey, itemId, data) => {
    console.log("[Modal:updateLoopedItem] Updating item:", { sectionKey, itemId, data });

    // Handle marking as completed separately
    if (data.completed === true) {
      await ModelPlannerService.markSectionDataComplete(session.id, itemId);
      // Remove completed from data to avoid saving it to answers
      const { completed, ...answers } = data;
      if (Object.keys(answers).length > 0) {
        await ModelPlannerService.updateSectionData(session.id, itemId, answers);
      }
    } else {
      await ModelPlannerService.updateSectionData(session.id, itemId, data);
    }
  };

  const upsertSingleSection = async (sectionKey, data) => {
    console.log("[upsertSingleSection] sectionKey:", sectionKey);
    console.log("[upsertSingleSection] data being sent:", data);
    console.log("[upsertSingleSection] data.completed:", data.completed);

    // For sidewalks, filter out ADA ramp fields that don't belong in sidewalks
    // ADA ramps are a nested loop within sidewalks, so their fields may be in currentItemData
    let dataToSave = data;
    if (sectionKey === "sidewalks") {
      const {
        is_used_on_job,
        ramp_type,
        ramp_detail_images,
        ramp_detail_sheet_number,
        ...sidewalksData
      } = data;
      console.log("[upsertSingleSection] Filtered out ADA fields from sidewalks data");
      dataToSave = sidewalksData;
    }

    // Extract completed flag - it goes on the record, not in answers
    const { completed, ...answers } = dataToSave;

    // Check if section data already exists
    const { data: existingData } = await ModelPlannerService.getAllSectionData(session.id, sectionKey);

    let result;
    if (existingData && existingData.length > 0) {
      // Update existing
      result = await ModelPlannerService.updateSectionData(session.id, existingData[0].id, answers);

      // Mark as completed if needed
      if (completed === true) {
        await ModelPlannerService.markSectionDataComplete(session.id, existingData[0].id);
      }
    } else {
      // Create new
      result = await ModelPlannerService.createSectionData(session.id, sectionKey, answers, 1);

      // Mark as completed if needed
      if (completed === true && result?.data?.id) {
        await ModelPlannerService.markSectionDataComplete(session.id, result.data.id);
      }
    }

    console.log("[upsertSingleSection] upsert result:", result);
    if (result?.error) {
      console.error("[upsertSingleSection] ERROR:", result.error);
    } else {
      console.log("[upsertSingleSection] SUCCESS - returned data:", result?.data);
    }

    return result;
  };

  const handleBack = () => {
    if (navigationHistory.length === 0) {
      return; // Nothing to go back to
    }

    // Pop the last state from history
    const previousState = navigationHistory[navigationHistory.length - 1];
    const newHistory = navigationHistory.slice(0, -1);

    setNavigationHistory(newHistory);
    setCurrentQuestion(previousState.question);
    setLoopState(previousState.loopState);
    setCurrentItemData(previousState.itemData);

    // Remove the last answered question from the list
    if (answeredQuestions.length > 0) {
      setAnsweredQuestions((prev) => prev.slice(0, -1));
    }
  };

  const handleAnswer = async (field, value, answerForHistory = null) => {
    // Handle mark complete command
    if (field === "_markComplete") {
      console.log(
        "[handleAnswer] Marking section as complete:",
        currentSection,
      );
      // Mark current item as completed in database
      // Check section type to determine how to save (not loopState, which could be from nested loops)
      const sectionConfig = wizardEngine.getSection(currentSection);

      if (
        sectionConfig.type === "looped" &&
        loopState &&
        loopState.itemIds[loopState.currentIndex]
      ) {
        // Looped section - update individual item
        const itemId = loopState.itemIds[loopState.currentIndex];
        console.log("[handleAnswer] Updating looped item:", itemId);
        await updateLoopedItem(currentSection, itemId, { completed: true });

        // Update local state
        setCurrentItemData((prev) => ({ ...prev, completed: true }));
      } else if (sectionConfig.type === "single") {
        // Single section - upsert with completed flag
        const updates = { ...currentItemData, completed: true };
        console.log(
          "[handleAnswer] Updating single section with data:",
          updates,
        );
        await upsertSingleSection(currentSection, updates);

        // Update local state
        setCurrentItemData(updates);
      }

      // Refresh session data to update left nav checkmarks
      console.log("[handleAnswer] Refreshing session data...");
      await refreshSessionData();
      console.log(
        "[handleAnswer] Refresh complete - left nav should now show checkmark",
      );
      return;
    }

    // Handle special navigation commands
    if (field === "_navigate") {
      // Use answerForHistory as the count if provided (from count questions)
      // Otherwise fall back to currentItemData._count
      const count =
        answerForHistory !== null ? answerForHistory : currentItemData._count;

      // Handle ADD_ITEMS_START directly (not processed by wizard engine)
      if (value === "ADD_ITEMS_START") {
        console.log("[handleAnswer] Handling ADD_ITEMS_START navigation");
        // Create a pseudo-result that handleNavigationResult expects
        const result = {
          questionId: "ADD_ITEMS_START",
          loopState: null,
          action: "navigate",
        };
        await handleNavigationResult(result, count);
        return;
      }

      const result = wizardEngine.getNextQuestion(
        currentSection,
        currentQuestion.id,
        { _navigate: value, _count: count },
        loopState,
      );

      // Pass currentItemData for edit mode checking (may be stale due to async state updates)
      await handleNavigationResult(result, answerForHistory, currentItemData);
      return;
    }

    if (field === "_count") {
      // Store count for looping
      setCurrentItemData((prev) => ({ ...prev, _count: value }));
      return;
    }

    // Store answer in current item data
    const updates = { ...currentItemData, [field]: value };
    setCurrentItemData(updates);

    // Also update ref immediately for synchronous access during navigation
    currentItemDataRef.current = updates;

    // Trigger auto-save
    autoSave(updates);
  };

  const handleNavigationResult = async (
    result,
    providedAnswer = null,
    explicitCurrentData = null,
  ) => {
    console.log("[handleNavigationResult] ===== NAVIGATION =====");
    console.log("[handleNavigationResult] result:", result);
    const { questionId, loopState: newLoopState, action } = result;
    console.log("[handleNavigationResult] questionId:", questionId);
    console.log("[handleNavigationResult] action:", action);
    console.log("[handleNavigationResult] currentSection:", currentSection);

    // Use explicitly passed data if available (for edit mode with updated answers),
    // otherwise use ref (which has latest synchronous updates),
    // otherwise fall back to currentItemData state
    const dataForEditModeCheck =
      explicitCurrentData || currentItemDataRef.current || currentItemData;
    console.log(
      "[handleNavigationResult] dataForEditModeCheck:",
      dataForEditModeCheck,
    );
    console.log(
      "[handleNavigationResult] Using data from:",
      explicitCurrentData
        ? "explicitCurrentData"
        : currentItemDataRef.current
          ? "ref"
          : "state",
    );

    // Add current question to answered questions (if it has a field)
    if (currentQuestion && currentQuestion.field) {
      const answerValue =
        providedAnswer !== null
          ? providedAnswer
          : currentItemData[currentQuestion.field];
      if (answerValue !== undefined) {
        setAnsweredQuestions((prev) => [
          ...prev,
          {
            question: currentQuestion,
            answer: answerValue,
          },
        ]);
      }
    }

    // Helper to load and flatten item data from JSONB storage
    const loadItemData = async (itemId) => {
      const { data: itemData } = await ModelPlannerService.getSectionData(session.id, itemId);
      if (itemData) {
        const { answers, ...rest } = itemData;
        return { ...rest, ...(answers || {}) };
      }
      return null;
    };

    // Helper to load single section data from JSONB storage
    const loadSingleSectionData = async (sectionKey) => {
      const { data: sectionData } = await ModelPlannerService.getAllSectionData(session.id, sectionKey);
      if (sectionData && sectionData.length > 0) {
        const { answers, ...rest } = sectionData[0];
        return { ...rest, ...(answers || {}) };
      }
      return null;
    };

    // Special handling for pond_confirmation - reload data from database
    const effectiveLoopState = newLoopState || loopState;
    if (questionId === "pond_confirmation" && effectiveLoopState) {
      const currentItemId = effectiveLoopState.itemIds[effectiveLoopState.currentIndex];
      if (currentItemId) {
        console.log("Loading pond data for confirmation, pond ID:", currentItemId);
        const pondData = await loadItemData(currentItemId);
        console.log("Retrieved pond data from database:", pondData);
        if (pondData) {
          setCurrentItemData(pondData);
        }
      }
    }

    // Special handling for curb_confirmation - reload data from database
    if (questionId === "curb_confirmation" && effectiveLoopState) {
      const currentItemId = effectiveLoopState.itemIds[effectiveLoopState.currentIndex];
      if (currentItemId) {
        const curbData = await loadItemData(currentItemId);
        if (curbData) {
          setCurrentItemData(curbData);
        }
      }
    }

    // Special handling for grading_confirmation - reload data from database
    if (questionId === "grading_confirmation" && session) {
      const gradingData = await loadSingleSectionData("grading");
      if (gradingData) {
        setCurrentItemData(gradingData);
      }
    }

    // Special handling for ditches_confirmation - reload data from database
    if (questionId === "ditches_confirmation" && session) {
      const ditchesData = await loadSingleSectionData("ditches");
      if (ditchesData) {
        setCurrentItemData(ditchesData);
      }
    }

    // Special handling for sidewalks_confirmation - reload data from database
    if (questionId === "sidewalks_confirmation" && session) {
      console.log("Loading sidewalks data for confirmation...");
      const sidewalksData = await loadSingleSectionData("sidewalks");
      console.log("Sidewalks data from database:", sidewalksData);
      if (sidewalksData) {
        setCurrentItemData(sidewalksData);
        console.log("Set currentItemData to:", sidewalksData);
      } else {
        console.warn("No sidewalks data found in database");
      }
    }

    // Special handling for building_pads_confirmation - reload data from database
    if (questionId === "building_pads_confirmation" && session) {
      console.log("[handleNavigationResult] Loading building pads data for confirmation...");
      const buildingPadsData = await loadSingleSectionData("building-pads");
      console.log("[handleNavigationResult] Building pads data from database:", buildingPadsData);
      if (buildingPadsData) {
        setCurrentItemData(buildingPadsData);
        console.log("[handleNavigationResult] Set currentItemData to:", buildingPadsData);
      } else {
        console.warn("[handleNavigationResult] No building pads data found in database");
      }
    }

    // Special handling for grate_elevations_confirmation - reload data from database
    if (questionId === "grate_elevations_confirmation" && session) {
      const grateElevationsData = await loadSingleSectionData("grate-elevations");
      if (grateElevationsData) {
        setCurrentItemData(grateElevationsData);
      }
    }

    if (action === "section_complete") {
      // For looped sections, save the "No" answer with completed flag
      // This handles when user says "No" to "Are there any ponds?" etc.
      const sectionConfig = wizardEngine.getSection(currentSection);
      if (sectionConfig && sectionConfig.type === "looped") {
        // Save the answer to the database (e.g., are_there_ponds: false, completed: true)
        // Use a special "section_status" table entry or use the single section pattern
        const sectionStatusData = {
          ...currentItemData,
          completed: true,
        };

        // Save to a status record for this looped section
        // For now, just mark the section itself as having no items
        console.log(
          "[handleNavigationResult] Looped section completed with no items, saving status:",
          sectionStatusData,
        );

        // We'll use the session update to track this
        // The checkmark will appear based on the section having 0 items but being "visited"
      }

      // Move to next section
      const nextSection = wizardEngine.getNextSection(currentSection);
      if (nextSection) {
        // Update session's current_section in database BEFORE navigating
        if (session) {
          await ModelPlannerService.updateSession(session.id, {
            current_section: nextSection,
          });
        }

        setCurrentSection(nextSection);
        const initialQ = wizardEngine.getInitialQuestion(nextSection);
        setCurrentQuestion(initialQ);
        setLoopState(null);
        setCurrentItemData({});
        setAnsweredQuestions([]); // Clear for new section

        // Refresh session data to update nav (will show section as complete)
        await refreshSessionData();
      } else {
        // All sections complete
        alert("All sections complete!");
        onClose();
      }
      return;
    }

    if (action === "loop_start") {
      console.log(
        "[Modal:handleNavigationResult] ===== LOOP_START ACTION =====",
      );
      console.log(
        "[Modal:handleNavigationResult] currentSection:",
        currentSection,
      );
      console.log("[Modal:handleNavigationResult] newLoopState:", newLoopState);
      console.log(
        "[Modal:handleNavigationResult] questionId to navigate to:",
        questionId,
      );
      console.log("[Modal:handleNavigationResult] session:", session);

      try {
        // Validate session exists
        if (!session || !session.id) {
          console.error(
            "[Modal:handleNavigationResult] ERROR: No active session",
          );
          alert(
            "Error: No active session found. Please refresh the page and try again.",
          );
          return;
        }

        // Pre-create all items for the loop
        console.log(
          "[Modal:handleNavigationResult] About to create",
          newLoopState.totalCount,
          "items",
        );
        const itemIds = await createAllLoopItems(
          currentSection,
          newLoopState.totalCount,
        );
        console.log(
          "[Modal:handleNavigationResult] createAllLoopItems returned itemIds:",
          itemIds,
        );

        // Validate that items were created
        if (!itemIds || itemIds.length === 0) {
          console.error(
            "[Modal:handleNavigationResult] ERROR: No items were created",
          );
          alert(
            `Failed to create ${currentSection} items in the database. Please check your internet connection and try again. If the problem persists, please contact support.`,
          );
          return;
        }

        if (itemIds.length !== newLoopState.totalCount) {
          console.error(
            `[Modal:handleNavigationResult] ERROR: Expected ${newLoopState.totalCount} items but only ${itemIds.length} were created`,
          );
          alert(
            `Warning: Only ${itemIds.length} of ${newLoopState.totalCount} items were created. Some items may have failed. Please review the console for details.`,
          );
        }

        const updatedLoopState = {
          ...newLoopState,
          itemIds,
        };
        console.log(
          "[Modal:handleNavigationResult] updatedLoopState:",
          updatedLoopState,
        );

        console.log(
          "[Modal:handleNavigationResult] Setting loop state and navigating to question:",
          questionId,
        );
        setLoopState(updatedLoopState);
        const nextQ = wizardEngine.getQuestion(currentSection, questionId);
        console.log("[Modal:handleNavigationResult] nextQ:", nextQ);

        if (!nextQ) {
          console.error(
            "[Modal:handleNavigationResult] ERROR: Could not find question:",
            questionId,
          );
          alert("Error: Could not load the next question. Please try again.");
          return;
        }

        setCurrentQuestion(nextQ);
        setCurrentItemData({});
        setAnsweredQuestions([]); // Clear for new pond

        // Refresh session data in background to update navigation panel (without resetting state)
        refreshSessionData();

        console.log(
          "[Modal:handleNavigationResult] Loop start complete, should now show question:",
          questionId,
        );
      } catch (error) {
        console.error(
          "[Modal:handleNavigationResult] ERROR in loop_start:",
          error,
        );
        alert(
          `An error occurred while setting up the ${currentSection} wizard: ${error.message}. Please try again or contact support if the problem persists.`,
        );
      }

      return;
    }

    // Handle LOOP_START_SINGLE - create one item at a time (new flow)
    if (action === "loop_start_single" || action === "ada_loop_start_single") {
      console.log(
        "[Modal:handleNavigationResult] ===== LOOP_START_SINGLE ACTION =====",
      );
      console.log(
        "[Modal:handleNavigationResult] currentSection:",
        currentSection,
      );
      console.log("[Modal:handleNavigationResult] newLoopState:", newLoopState);

      try {
        // Validate session exists
        if (!session || !session.id) {
          console.error(
            "[Modal:handleNavigationResult] ERROR: No active session",
          );
          alert(
            "Error: No active session found. Please refresh the page and try again.",
          );
          return;
        }

        // Create just the first item
        console.log(
          "[Modal:handleNavigationResult] Creating first item for",
          currentSection,
        );
        const itemIds = await createAllLoopItems(currentSection, 1);
        console.log(
          "[Modal:handleNavigationResult] Created item with ID:",
          itemIds,
        );

        if (!itemIds || itemIds.length === 0) {
          console.error(
            "[Modal:handleNavigationResult] ERROR: Failed to create item",
          );
          alert(`Failed to create ${currentSection} item. Please try again.`);
          return;
        }

        const updatedLoopState = {
          ...newLoopState,
          itemIds,
        };

        setLoopState(updatedLoopState);
        const nextQ = wizardEngine.getQuestion(currentSection, questionId);
        setCurrentQuestion(nextQ);
        setCurrentItemData({});
        setAnsweredQuestions([]);

        // Refresh session data to show new item in nav
        refreshSessionData();

        console.log(
          "[Modal:handleNavigationResult] Loop start single complete",
        );
      } catch (error) {
        console.error(
          "[Modal:handleNavigationResult] ERROR in loop_start_single:",
          error,
        );
        alert(
          `An error occurred while creating ${currentSection}. Please try again.`,
        );
      }

      return;
    }

    // Handle LOOP_CONTINUE_ADD - add another item (user clicked "Yes" to add another)
    if (action === "loop_continue_add" || action === "ada_loop_continue_add") {
      console.log(
        "[Modal:handleNavigationResult] ===== LOOP_CONTINUE_ADD ACTION =====",
      );

      try {
        // Create a new item
        const newIndex = newLoopState.currentIndex;
        const newItemId = await createLoopedItem(currentSection, newIndex, {});

        if (!newItemId) {
          console.error(
            "[Modal:handleNavigationResult] ERROR: Failed to create item",
          );
          alert(`Failed to create ${currentSection} item. Please try again.`);
          return;
        }

        // Update loop state with new item
        const updatedItemIds = [...newLoopState.itemIds, newItemId];
        const updatedLoopState = {
          ...newLoopState,
          itemIds: updatedItemIds,
        };

        setLoopState(updatedLoopState);
        const nextQ = wizardEngine.getQuestion(currentSection, questionId);
        setCurrentQuestion(nextQ);
        setCurrentItemData({});
        setAnsweredQuestions([]);

        // Refresh session data to show new item in nav
        refreshSessionData();

        console.log(
          "[Modal:handleNavigationResult] Continue add complete, created item:",
          newItemId,
        );
      } catch (error) {
        console.error(
          "[Modal:handleNavigationResult] ERROR in loop_continue_add:",
          error,
        );
        alert(
          `An error occurred while adding ${currentSection}. Please try again.`,
        );
      }

      return;
    }

    // Handle ADD_ITEMS_START - for adding items from summary page
    if (questionId === "ADD_ITEMS_START") {
      console.log("[Modal:handleNavigationResult] ===== ADD_ITEMS_START =====");
      console.log(
        "[Modal:handleNavigationResult] providedAnswer:",
        providedAnswer,
      );
      console.log(
        "[Modal:handleNavigationResult] currentItemData:",
        currentItemData,
      );
      console.log(
        "[Modal:handleNavigationResult] currentQuestion:",
        currentQuestion,
      );
      console.log(
        "[Modal:handleNavigationResult] currentSection:",
        currentSection,
      );

      // Use providedAnswer (from count question) as it's passed synchronously,
      // fallback to currentItemData if not available (e.g., from edit mode)
      const count = providedAnswer || currentItemData._addItemsCount;
      // Read section key from question itself (avoids async state issues)
      const sectionKey =
        currentQuestion._sectionKey || currentItemData._addItemsSectionKey;

      console.log("[Modal:handleNavigationResult] Extracted count:", count);
      console.log(
        "[Modal:handleNavigationResult] Extracted sectionKey:",
        sectionKey,
      );

      if (!count || !sectionKey) {
        console.error(
          "[Modal:handleNavigationResult] Missing count or section key for adding items",
          { count, sectionKey },
        );
        alert("Error: Missing information to add items. Please try again.");
        return;
      }

      // Get existing items to calculate starting order number
      const dataKey = getSectionDataKey(sectionKey);
      const existingItems = sessionData[dataKey] || [];
      const startingOrder = existingItems.length;

      // Create all new items using unified JSONB storage
      const newItemIds = [];
      for (let i = 0; i < count; i++) {
        const orderNum = startingOrder + i;
        const { data: created } = await ModelPlannerService.createSectionData(
          session.id,
          sectionKey,
          {},
          orderNum
        );
        if (created?.id) {
          newItemIds.push(created.id);
        }
      }

      // Refresh session data to include new items
      await refreshSessionData();

      // Build complete itemIds array (existing + new)
      const allItemIds = [
        ...existingItems.map((item) => item.id),
        ...newItemIds,
      ];

      // Special handling for ADA ramps (nested loop within sidewalks)
      if (sectionKey === "ada-ramps") {
        // ADA ramps use sidewalks section for questions
        const addItemsLoopState = {
          totalCount: allItemIds.length,
          currentIndex: startingOrder, // Start at first new item
          itemIds: allItemIds,
          loopStartQuestion: "ada_ramp_type",
          isEditMode: true,
          returnToSummary: false, // Return to sidewalks confirmation instead
          isADALoop: true, // Flag this as an ADA loop
          addingItemsEndIndex: allItemIds.length - 1, // Track where to stop
        };

        // Load data for the first new ADA ramp using unified JSONB storage
        const currentItemId = allItemIds[startingOrder];
        let initialItemData = {};
        if (currentItemId) {
          const { data: itemData } = await ModelPlannerService.getSectionData(session.id, currentItemId);
          if (itemData) {
            // Flatten answers to top level for compatibility
            const { answers, ...rest } = itemData;
            initialItemData = { ...rest, ...(answers || {}) };
          }
        }

        setLoopState(addItemsLoopState);
        setCurrentSection("sidewalks"); // Keep parent section
        const startQuestion = wizardEngine.getQuestion(
          "sidewalks",
          "ada_ramp_type",
        );
        setCurrentQuestion(startQuestion);
        setCurrentItemData(initialItemData);
        setAnsweredQuestions([]);
        setNavigationHistory([]);

        return;
      }

      // Load data for the first new item using unified JSONB storage
      const currentItemId = allItemIds[startingOrder];
      let initialItemData = {};
      if (currentItemId) {
        const { data: itemData } = await ModelPlannerService.getSectionData(session.id, currentItemId);
        if (itemData) {
          // Flatten answers to top level for compatibility
          const { answers, ...rest } = itemData;
          initialItemData = { ...rest, ...(answers || {}) };
        }
      }

      // Set up loop state for the new items only (for regular looped sections)
      const loopStartQuestion = wizardEngine.getLoopStartQuestion(sectionKey);
      const addItemsLoopState = {
        totalCount: allItemIds.length,
        currentIndex: startingOrder, // Start at first new item
        itemIds: allItemIds,
        loopStartQuestion: loopStartQuestion,
        isEditMode: true,
        returnToSummary: true, // Return to summary after all new items
        addingItemsEndIndex: allItemIds.length - 1, // Track where to stop
      };

      setLoopState(addItemsLoopState);
      setCurrentSection(sectionKey);
      const startQuestion = wizardEngine.getQuestion(
        sectionKey,
        loopStartQuestion,
      );
      setCurrentQuestion(startQuestion);
      setCurrentItemData(initialItemData);
      setAnsweredQuestions([]);
      setNavigationHistory([]);

      return;
    }

    if (action === "ada_loop_start") {
      // ADA ramps are a nested loop within sidewalks section
      // Pre-create all ADA ramp items
      const itemIds = await createAllLoopItems(
        "ada-ramps",
        newLoopState.totalCount,
      );

      const updatedLoopState = {
        ...newLoopState,
        itemIds,
      };

      setLoopState(updatedLoopState);
      const nextQ = wizardEngine.getQuestion(currentSection, questionId);
      setCurrentQuestion(nextQ);
      setCurrentItemData({});
      setAnsweredQuestions([]); // Clear for new ADA ramp

      // Refresh session data in background to update navigation panel (without resetting state)
      refreshSessionData();

      return;
    }

    if (action === "loop_continue") {
      setLoopState(newLoopState);
      const nextQ = wizardEngine.getQuestion(currentSection, questionId);
      setCurrentQuestion(nextQ);
      setCurrentItemData({});
      setAnsweredQuestions([]); // Clear for next pond
      return;
    }

    if (action === "loop_complete" || action === "ada_loop_complete") {
      // Check if we came from edit mode on a summary page
      if (loopState?.returnToSummary && loopState?.isEditMode) {
        // Return to section summary instead of continuing the loop
        const summaryQuestion = {
          id: "section_summary",
          type: "section_summary",
          text: `${wizardEngine.getSection(currentSection).label} Summary`,
          sectionKey: currentSection,
        };

        setCurrentQuestion(summaryQuestion);
        setLoopState(null);
        setCurrentItemData({});
        setAnsweredQuestions([]);

        // Refresh session data to show updated item
        refreshSessionData();
        return;
      }

      // ADA loop complete - go to sidewalks confirmation
      if (action === "ada_loop_complete") {
        console.log("[ada_loop_complete] Loading sidewalks data for confirmation...");
        const sidewalksData = await loadSingleSectionData("sidewalks");

        const confirmationQ = wizardEngine.getQuestion(
          currentSection,
          "sidewalks_confirmation",
        );
        if (confirmationQ && sidewalksData) {
          setCurrentQuestion(confirmationQ);
          setLoopState(null);
          setCurrentItemData(sidewalksData); // Load sidewalks data instead of clearing
          setAnsweredQuestions([]);
          // Don't call loadSession() - let user see confirmation with all data
          return;
        }
      }

      // Loop done - advance to next section
      setLoopState(null);
      setCurrentItemData({});
      setAnsweredQuestions([]);

      // Get next section and navigate to it
      const nextSection = wizardEngine.getNextSection(currentSection);
      if (nextSection) {
        // Update session's current_section in database BEFORE reloading
        if (session) {
          await ModelPlannerService.updateSession(session.id, {
            current_section: nextSection,
          });
        }

        setCurrentSection(nextSection);
        const nextQ = wizardEngine.getInitialQuestion(nextSection);
        setCurrentQuestion(nextQ);

        // Reload session data to get fresh data for new section
        await loadSession();
      } else {
        // No more sections - wizard complete
        console.log("Wizard complete!");
      }

      return;
    }

    // Regular navigation
    if (questionId) {
      console.log("[handleNavigationResult] ===== REGULAR NAVIGATION =====");
      console.log(
        "[handleNavigationResult] Regular navigation to questionId:",
        questionId,
      );
      console.log(
        "[handleNavigationResult] singleQuestionEditMode:",
        singleQuestionEditMode,
      );
      console.log(
        "[handleNavigationResult] singleQuestionEditMode.active:",
        singleQuestionEditMode?.active,
      );
      console.log(
        "[handleNavigationResult] singleQuestionEditMode.targetQuestionId:",
        singleQuestionEditMode?.targetQuestionId,
      );
      console.log(
        "[handleNavigationResult] questionId === targetQuestionId?",
        questionId === singleQuestionEditMode?.targetQuestionId,
      );

      // Check if we're in single question edit mode - if so, return to summary or confirmation instead of continuing
      // BUT allow navigation TO the target question (only prevent navigation PAST it)
      // ALSO allow navigation to conditional follow-up questions (onYes/onNo paths)
      if (
        singleQuestionEditMode &&
        singleQuestionEditMode.active &&
        questionId !== singleQuestionEditMode.targetQuestionId
      ) {
        // Use the new recursive path checking to handle nested boolean questions
        // Use dataForEditModeCheck which contains the latest answer (even if state hasn't updated yet)
        const allowedQuestions = wizardEngine.getAllowedQuestionsInEditMode(
          currentSection,
          singleQuestionEditMode.targetQuestionId,
          dataForEditModeCheck,
        );

        const isInConditionalPath = allowedQuestions.has(questionId);

        console.log(
          "[handleNavigationResult] Allowed questions in edit mode:",
          Array.from(allowedQuestions),
        );
        console.log("[handleNavigationResult] Current questionId:", questionId);
        console.log(
          "[handleNavigationResult] Is questionId allowed?",
          isInConditionalPath,
        );

        // If not in conditional path, block navigation and return to summary/confirmation
        if (!isInConditionalPath) {
          console.log(
            "[handleNavigationResult] Single question edit mode active - returning instead of continuing to",
            questionId,
          );

          const returnToConfirmation =
            singleQuestionEditMode.returnToConfirmation;

          // Clear edit mode
          setSingleQuestionEditMode(null);

          if (returnToConfirmation) {
            // Return to confirmation page (when editing from confirmation during wizard flow)
            console.log(
              "[handleNavigationResult] Returning to confirmation page",
            );

            // Get the confirmation question ID for the current section
            const confirmationQuestionId =
              currentSection === "ponds"
                ? "pond_confirmation"
                : currentSection === "curb"
                  ? "curb_confirmation"
                  : currentSection === "walls"
                    ? "wall_confirmation"
                    : currentSection === "grading"
                      ? "grading_confirmation"
                      : currentSection === "ditches"
                        ? "ditches_confirmation"
                        : currentSection === "sidewalks"
                          ? "sidewalks_confirmation"
                          : currentSection === "ada-ramps"
                            ? "ada_ramp_confirmation"
                            : currentSection === "building-pads"
                              ? "building_pads_confirmation"
                              : currentSection === "grate-elevations"
                                ? "grate_elevations_confirmation"
                                : "confirmation";

            // OPTIMISTIC UPDATE: Use currentItemData directly instead of fetching from database
            // This ensures the confirmation page shows the updated value instantly
            console.log(
              "[handleNavigationResult] Using currentItemData for immediate display:",
              currentItemData,
            );

            // Also update sessionData so subsequent views show the updated data
            if (currentSection === "building-pads") {
              setSessionData((prev) => ({
                ...prev,
                buildingPads: currentItemData,
              }));
            } else if (currentSection === "grading") {
              setSessionData((prev) => ({
                ...prev,
                gradingSections: currentItemData,
              }));
            } else if (currentSection === "ditches") {
              setSessionData((prev) => ({
                ...prev,
                ditchesSwales: currentItemData,
              }));
            } else if (currentSection === "sidewalks") {
              setSessionData((prev) => ({
                ...prev,
                sidewalks: currentItemData,
              }));
            } else if (currentSection === "grate-elevations") {
              setSessionData((prev) => ({
                ...prev,
                grateElevations: currentItemData,
              }));
            } else if (loopState && loopState.itemIds[loopState.currentIndex]) {
              // For looped sections, update the specific item in the array
              const itemIndex = loopState.currentIndex;

              if (currentSection === "ponds") {
                setSessionData((prev) => ({
                  ...prev,
                  ponds: prev.ponds.map((pond, idx) =>
                    idx === itemIndex ? currentItemData : pond,
                  ),
                }));
              } else if (currentSection === "curb") {
                setSessionData((prev) => ({
                  ...prev,
                  curbTypes: prev.curbTypes.map((curb, idx) =>
                    idx === itemIndex ? currentItemData : curb,
                  ),
                }));
              } else if (currentSection === "walls") {
                setSessionData((prev) => ({
                  ...prev,
                  walls: prev.walls.map((wall, idx) =>
                    idx === itemIndex ? currentItemData : wall,
                  ),
                }));
              }
            }

            const confirmationQ = wizardEngine.getQuestion(
              currentSection,
              confirmationQuestionId,
            );
            setCurrentQuestion(confirmationQ);
            setAnsweredQuestions([]);
            setNavigationHistory([]);

            // Refresh session data in background to confirm (but UI already shows optimistic update)
            refreshSessionData();
          } else {
            // Return to section summary (when editing from summary view)
            console.log("[handleNavigationResult] Returning to summary view");

            // OPTIMISTIC UPDATE: Update sessionData immediately with current changes
            // This ensures the summary shows the updated value instantly
            console.log(
              "[handleNavigationResult] Applying optimistic update with currentItemData:",
              currentItemData,
            );

            if (currentSection === "building-pads") {
              setSessionData((prev) => ({
                ...prev,
                buildingPads: currentItemData,
              }));
            } else if (currentSection === "grading") {
              setSessionData((prev) => ({
                ...prev,
                gradingSections: currentItemData,
              }));
            } else if (currentSection === "ditches") {
              setSessionData((prev) => ({
                ...prev,
                ditchesSwales: currentItemData,
              }));
            } else if (currentSection === "sidewalks") {
              setSessionData((prev) => ({
                ...prev,
                sidewalks: currentItemData,
              }));
            } else if (currentSection === "grate-elevations") {
              setSessionData((prev) => ({
                ...prev,
                grateElevations: currentItemData,
              }));
            } else if (loopState && loopState.itemIds[loopState.currentIndex]) {
              // For looped sections, update the specific item in the array
              const itemIndex = loopState.currentIndex;

              if (currentSection === "ponds") {
                setSessionData((prev) => ({
                  ...prev,
                  ponds: prev.ponds.map((pond, idx) =>
                    idx === itemIndex ? currentItemData : pond,
                  ),
                }));
              } else if (currentSection === "curb") {
                setSessionData((prev) => ({
                  ...prev,
                  curbTypes: prev.curbTypes.map((curb, idx) =>
                    idx === itemIndex ? currentItemData : curb,
                  ),
                }));
              } else if (currentSection === "walls") {
                setSessionData((prev) => ({
                  ...prev,
                  walls: prev.walls.map((wall, idx) =>
                    idx === itemIndex ? currentItemData : wall,
                  ),
                }));
              }
            }

            const summaryQuestion = {
              id: "section_summary",
              type: "section_summary",
              text: `${wizardEngine.getSection(currentSection).label} Summary`,
              sectionKey: currentSection,
            };

            setCurrentQuestion(summaryQuestion);
            setLoopState(null);
            setCurrentItemData({});
            setAnsweredQuestions([]);
            setNavigationHistory([]);

            // Refresh session data in background to confirm (but UI already shows optimistic update)
            refreshSessionData();
          }

          return;
        } else {
          // Question is in conditional path - allow navigation to continue
          console.log(
            "[handleNavigationResult] Allowing navigation to conditional follow-up question:",
            questionId,
          );
        }
      }

      console.log(
        "[handleNavigationResult] Proceeding with navigation - NOT blocked by edit mode",
      );
      console.log(
        "[handleNavigationResult] This means either: edit mode is OFF, OR navigating TO the target question",
      );

      // Save current state to history before navigating
      setNavigationHistory((prev) => [
        ...prev,
        {
          question: currentQuestion,
          loopState: loopState,
          itemData: { ...currentItemData },
        },
      ]);

      console.log(
        "[handleNavigationResult] Getting question from wizardEngine for:",
        currentSection,
        questionId,
      );
      const nextQ = wizardEngine.getQuestion(currentSection, questionId);
      console.log("[handleNavigationResult] Retrieved question:", nextQ);
      console.log("[handleNavigationResult] Question type:", nextQ?.type);
      console.log("[handleNavigationResult] Question id:", nextQ?.id);
      console.log(
        "[handleNavigationResult] About to call setCurrentQuestion with:",
        nextQ,
      );

      setCurrentQuestion(nextQ);
      setLoopState(newLoopState);

      console.log(
        "[handleNavigationResult] Completed setCurrentQuestion and setLoopState calls",
      );

      // Auto-scroll to bottom to show new question
      setTimeout(() => {
        if (questionAreaRef.current) {
          questionAreaRef.current.scrollTop =
            questionAreaRef.current.scrollHeight;
        }
      }, 100);
    }
  };

  const handleEditFromConfirmation = (questionId) => {
    console.log("[handleEditFromConfirmation] ===== MODAL EDIT HANDLER =====");
    console.log(
      "[handleEditFromConfirmation] Called with questionId:",
      questionId,
    );
    console.log("[handleEditFromConfirmation] currentSection:", currentSection);
    console.log(
      "[handleEditFromConfirmation] currentQuestion:",
      currentQuestion,
    );
    console.log(
      "[handleEditFromConfirmation] current singleQuestionEditMode:",
      singleQuestionEditMode,
    );

    // Called when editing from a confirmation page during wizard flow
    // Sets edit mode to return to confirmation after editing
    const editMode = {
      active: true,
      sectionKey: currentSection,
      targetQuestionId: questionId,
      returnToConfirmation: true, // Flag to return to confirmation instead of summary
    };

    console.log(
      "[handleEditFromConfirmation] Setting singleQuestionEditMode to:",
      editMode,
    );
    setSingleQuestionEditMode(editMode);
    console.log(
      "[handleEditFromConfirmation] Completed setSingleQuestionEditMode call",
    );
  };

  const handleItemClick = async (
    sectionKey,
    item,
    itemIndex,
    targetQuestionId = null,
  ) => {
    // Special handling for ADA ramps (nested loop within sidewalks)
    if (sectionKey === "ada-ramps") {
      // ADA ramps are not a top-level section - they're a nested loop
      // Set parent section (sidewalks) as current section
      setCurrentSection("sidewalks");

      // Get all ADA ramps to build itemIds array
      const adaRamps = sessionData.adaRamps || [];
      const itemIds = adaRamps.map((r) => r.id);

      // Set up ADA loop state
      const adaLoopState = {
        totalCount: adaRamps.length,
        currentIndex: itemIndex,
        itemIds: itemIds,
        loopStartQuestion: "ada_ramp_type",
        isEditMode: true,
        returnToSummary: false, // Return to sidewalks confirmation instead
        isADALoop: true, // Flag this as an ADA loop
      };
      setLoopState(adaLoopState);

      // Load the ADA ramp's existing data
      setCurrentItemData(item);

      // If targetQuestionId is provided, navigate directly to that question
      if (targetQuestionId) {
        const targetQuestion = wizardEngine.getQuestion(
          "sidewalks",
          targetQuestionId,
        );
        setCurrentQuestion(targetQuestion);
        setAnsweredQuestions([]);
        setNavigationHistory([]);

        // Set single question edit mode - after answering this question, return to sidewalks confirmation
        setSingleQuestionEditMode({
          active: true,
          sectionKey: "sidewalks",
          targetQuestionId: targetQuestionId,
          returnToConfirmation: true,
        });
        return;
      }

      // Navigate to ADA ramp confirmation
      const confirmationQuestion = wizardEngine.getQuestion(
        "sidewalks",
        "ada_ramp_confirmation",
      );
      setCurrentQuestion(confirmationQuestion);

      // Build answered questions for display on confirmation page
      const previouslyAnswered = wizardEngine.getAnsweredQuestionsInOrder(
        "sidewalks",
        item,
      );
      setAnsweredQuestions(previouslyAnswered);

      // Build navigation history for back button from confirmation
      const navHistory = previouslyAnswered.map((aq, index) => ({
        question: aq.question,
        loopState: adaLoopState,
        itemData: { ...item },
      }));
      setNavigationHistory(navHistory);

      return;
    }

    // Load the item's data and navigate to appropriate starting point
    setCurrentSection(sectionKey);

    // Check if this is a single section or looped section
    const sectionConfig = wizardEngine.getSection(sectionKey);

    if (sectionConfig.type === "single") {
      // Single section - no loop state needed
      setLoopState(null);
      setCurrentItemData(item);

      // If targetQuestionId is provided, navigate directly to that question
      if (targetQuestionId) {
        const targetQuestion = wizardEngine.getQuestion(
          sectionKey,
          targetQuestionId,
        );
        setCurrentQuestion(targetQuestion);
        setAnsweredQuestions([]);

        // Build navigation history with section_summary as the back target
        const summaryQuestion = {
          id: "section_summary",
          type: "section_summary",
          text: `${sectionConfig.label} Summary`,
          sectionKey: sectionKey,
        };
        setNavigationHistory([
          {
            question: summaryQuestion,
            loopState: null,
            itemData: { ...item },
          },
        ]);

        // Set single question edit mode - after answering this question, return to summary
        setSingleQuestionEditMode({
          active: true,
          sectionKey: sectionKey,
          targetQuestionId: targetQuestionId,
          returnToConfirmation: false, // Explicitly set to return to summary (not confirmation)
        });
        return;
      }

      // Check if item is partially complete or fully complete
      const resumePoint = wizardEngine.findNextUnansweredQuestion(
        sectionKey,
        item,
      );

      if (resumePoint) {
        // Item is partially complete - resume from next unanswered question
        const nextQuestion = wizardEngine.getQuestion(
          sectionKey,
          resumePoint.questionId,
        );
        setCurrentQuestion(nextQuestion);
        setAnsweredQuestions(resumePoint.answeredQuestions);

        // Build navigation history from answered questions so back button works
        const navHistory = resumePoint.answeredQuestions.map((aq, index) => ({
          question: aq.question,
          loopState: null,
          itemData: { ...item },
        }));
        setNavigationHistory(navHistory);
      } else {
        // Item is complete (or empty) - show confirmation page
        const confirmationQuestionId =
          sectionKey === "grading"
            ? "grading_confirmation"
            : sectionKey === "ditches"
              ? "ditches_confirmation"
              : sectionKey === "sidewalks"
                ? "sidewalks_confirmation"
                : sectionKey === "building-pads"
                  ? "building_pads_confirmation"
                  : sectionKey === "grate-elevations"
                    ? "grate_elevations_confirmation"
                    : "confirmation";

        const confirmationQuestion = wizardEngine.getQuestion(
          sectionKey,
          confirmationQuestionId,
        );
        setCurrentQuestion(confirmationQuestion);

        // Build answered questions for display on confirmation page
        const previouslyAnswered = wizardEngine.getAnsweredQuestionsInOrder(
          sectionKey,
          item,
        );
        setAnsweredQuestions(previouslyAnswered);

        // Build navigation history for back button from confirmation
        const navHistory = previouslyAnswered.map((aq, index) => ({
          question: aq.question,
          loopState: null,
          itemData: { ...item },
        }));
        setNavigationHistory(navHistory);
      }
    } else {
      // Looped section - build itemIds array and loop state
      const dataKey = getSectionDataKey(sectionKey);
      const sectionData = sessionData[dataKey];

      // Ensure we have an array
      const items = Array.isArray(sectionData) ? sectionData : [];
      const itemIds = items.map((i) => i.id);

      // Get the loop start question for this section
      const loopStartQuestion = wizardEngine.getLoopStartQuestion(sectionKey);

      // Set up loop state to edit this specific item
      const newLoopState = {
        totalCount: items.length,
        currentIndex: itemIndex,
        itemIds: itemIds,
        loopStartQuestion: loopStartQuestion,
        isEditMode: true,
        returnToSummary: true, // Flag to return to summary after confirm
      };
      setLoopState(newLoopState);

      // Load the item's existing data
      setCurrentItemData(item);

      // If targetQuestionId is provided, navigate directly to that question
      if (targetQuestionId) {
        const targetQuestion = wizardEngine.getQuestion(
          sectionKey,
          targetQuestionId,
        );
        setCurrentQuestion(targetQuestion);
        setAnsweredQuestions([]);

        // Build navigation history with section_summary as the back target
        const summaryQuestion = {
          id: "section_summary",
          type: "section_summary",
          text: `${sectionConfig.label} Summary`,
          sectionKey: sectionKey,
        };
        setNavigationHistory([
          {
            question: summaryQuestion,
            loopState: newLoopState,
            itemData: { ...item },
          },
        ]);

        // Set single question edit mode - after answering this question, return to summary
        setSingleQuestionEditMode({
          active: true,
          sectionKey: sectionKey,
          targetQuestionId: targetQuestionId,
          returnToConfirmation: false, // Explicitly set to return to summary (not confirmation)
        });
        return;
      }

      // Check if item is partially complete or fully complete
      const resumePoint = wizardEngine.findNextUnansweredQuestion(
        sectionKey,
        item,
      );

      if (resumePoint) {
        // Item is partially complete - resume from next unanswered question
        const nextQuestion = wizardEngine.getQuestion(
          sectionKey,
          resumePoint.questionId,
        );
        setCurrentQuestion(nextQuestion);
        setAnsweredQuestions(resumePoint.answeredQuestions);

        // Build navigation history from answered questions so back button works
        const navHistory = resumePoint.answeredQuestions.map((aq, index) => ({
          question: aq.question,
          loopState: newLoopState,
          itemData: { ...item },
        }));
        setNavigationHistory(navHistory);
      } else {
        // Item is complete (or empty) - show confirmation page
        const confirmationQuestion = wizardEngine.getQuestion(
          sectionKey,
          sectionKey === "ponds"
            ? "pond_confirmation"
            : sectionKey === "curb"
              ? "curb_confirmation"
              : sectionKey === "walls"
                ? "wall_confirmation"
                : sectionKey === "ada-ramps"
                  ? "ada_ramp_confirmation"
                  : "confirmation",
        );
        setCurrentQuestion(confirmationQuestion);

        // Build answered questions for display on confirmation page
        const previouslyAnswered = wizardEngine.getAnsweredQuestionsInOrder(
          sectionKey,
          item,
        );
        setAnsweredQuestions(previouslyAnswered);

        // Build navigation history for back button from confirmation
        const navHistory = previouslyAnswered.map((aq, index) => ({
          question: aq.question,
          loopState: newLoopState,
          itemData: { ...item },
        }));
        setNavigationHistory(navHistory);
      }
    }
  };

  const getSectionItemLabel = (sectionKey) => {
    const labelMap = {
      ponds: "Pond",
      curb: "Curb Type",
      walls: "Wall",
      "ada-ramps": "ADA Ramp",
      grading: "Section",
      ditches: "Item",
      sidewalks: "Item",
      "building-pads": "Item",
      "grate-elevations": "Item",
    };
    return labelMap[sectionKey] || "Item";
  };

  const handleSectionSelect = (sectionKey) => {
    console.log("[handleSectionSelect] ===== SELECTING SECTION =====");
    console.log("[handleSectionSelect] sectionKey:", sectionKey);
    setCurrentSection(sectionKey);
    setLoopState(null);
    setSingleQuestionEditMode(null); // Clear edit mode when changing sections

    // For single sections, load existing data if available
    const sectionConfig = wizardEngine.getSection(sectionKey);
    console.log("[handleSectionSelect] sectionConfig:", sectionConfig);

    if (sectionConfig && sectionConfig.type === "single") {
      const dataKey = getSectionDataKey(sectionKey);
      const existingData = sessionData[dataKey];
      console.log("[handleSectionSelect] dataKey:", dataKey);
      console.log(
        "[handleSectionSelect] existingData:",
        JSON.stringify(existingData, null, 2),
      );
      console.log(
        "[handleSectionSelect] existingData.completed:",
        existingData?.completed,
      );

      // Check if section is marked as completed using helper
      const isCompleted = checkSectionCompletion(sectionKey, existingData);

      if (isCompleted) {
        // Section is completed - show section summary view
        const summaryQuestion = {
          id: "section_summary",
          type: "section_summary",
          text: `${wizardEngine.getSection(sectionKey).label} Summary`,
          sectionKey: sectionKey,
        };
        console.log("[handleSectionSelect] Showing summary view (completed)");
        setCurrentQuestion(summaryQuestion);
        setCurrentItemData({});
        setAnsweredQuestions([]);
      } else if (existingData && Object.keys(existingData).length > 0) {
        // Data exists but not completed - use intelligent resume logic
        console.log(
          "[handleSectionSelect] Data exists but not completed - finding resume point",
        );
        setCurrentItemData(existingData);

        // Check if section is partially complete or all questions answered
        const resumePoint = wizardEngine.findNextUnansweredQuestion(
          sectionKey,
          existingData,
        );

        if (resumePoint) {
          // Section is partially complete - resume from next unanswered question
          console.log(
            "[handleSectionSelect] Resuming from question:",
            resumePoint.questionId,
          );
          const nextQuestion = wizardEngine.getQuestion(
            sectionKey,
            resumePoint.questionId,
          );
          setCurrentQuestion(nextQuestion);
          setAnsweredQuestions(resumePoint.answeredQuestions);

          // Build navigation history from answered questions so back button works
          const navHistory = resumePoint.answeredQuestions.map((aq, index) => ({
            question: aq.question,
            loopState: null,
            itemData: { ...existingData },
          }));
          setNavigationHistory(navHistory);
        } else {
          // All questions answered - show confirmation page
          console.log(
            "[handleSectionSelect] All questions answered - showing confirmation page",
          );
          const confirmationQuestionId =
            sectionKey === "grading"
              ? "grading_confirmation"
              : sectionKey === "ditches"
                ? "ditches_confirmation"
                : sectionKey === "sidewalks"
                  ? "sidewalks_confirmation"
                  : sectionKey === "building-pads"
                    ? "building_pads_confirmation"
                    : sectionKey === "grate-elevations"
                      ? "grate_elevations_confirmation"
                      : "confirmation";

          const confirmationQuestion = wizardEngine.getQuestion(
            sectionKey,
            confirmationQuestionId,
          );
          setCurrentQuestion(confirmationQuestion);

          // Build answered questions for display on confirmation page
          const previouslyAnswered = wizardEngine.getAnsweredQuestionsInOrder(
            sectionKey,
            existingData,
          );
          setAnsweredQuestions(previouslyAnswered);

          // Build navigation history for back button from confirmation
          const navHistory = previouslyAnswered.map((aq, index) => ({
            question: aq.question,
            loopState: null,
            itemData: { ...existingData },
          }));
          setNavigationHistory(navHistory);
        }
      } else {
        // No existing data, start fresh from first question
        const initialQ = wizardEngine.getInitialQuestion(sectionKey);
        console.log(
          "[handleSectionSelect] Starting fresh from initial question:",
          initialQ,
        );
        setCurrentQuestion(initialQ);
        setCurrentItemData({});
        setAnsweredQuestions([]);
      }
    } else {
      // For looped sections, check if all items are completed using helper
      const dataKey = getSectionDataKey(sectionKey);
      const sectionData = sessionData[dataKey];
      const allItemsCompleted = checkSectionCompletion(sectionKey, sectionData);

      if (allItemsCompleted) {
        // Show section summary if all items completed
        const summaryQuestion = {
          id: "section_summary",
          type: "section_summary",
          text: `${wizardEngine.getSection(sectionKey).label} Summary`,
          sectionKey: sectionKey,
        };
        setCurrentQuestion(summaryQuestion);
        setCurrentItemData({});
        setAnsweredQuestions([]);
      } else {
        // Start fresh if section not completed
        const initialQ = wizardEngine.getInitialQuestion(sectionKey);
        setCurrentQuestion(initialQ);
        setCurrentItemData({});
        setAnsweredQuestions([]);
      }
    }
  };

  const handleSectionSummaryClick = (sectionKey) => {
    // Show comprehensive summary for a looped section
    setCurrentSection(sectionKey);

    // Create a special "section_summary" question to display all items
    const summaryQuestion = {
      id: "section_summary",
      type: "section_summary",
      text: `${wizardEngine.getSection(sectionKey).label} Summary`,
      sectionKey: sectionKey,
    };

    setCurrentQuestion(summaryQuestion);
    setLoopState(null);
    setCurrentItemData({});
    setAnsweredQuestions([]);
    setSingleQuestionEditMode(null); // Clear edit mode when going to summary
  };

  const handleAddAnotherItem = (sectionKey) => {
    // Show a count question to ask how many items to add
    const itemLabel = getSectionItemLabel(sectionKey);
    const countQuestion = {
      id: "add_items_count",
      type: "count",
      field: "_addItemsCount",
      text: `How many ${itemLabel.toLowerCase()}s do you want to add?`,
      next: "ADD_ITEMS_START",
      _sectionKey: sectionKey, // Store section key in question to avoid async state issues
    };

    // Store the section key for later use (kept for backwards compatibility)
    setCurrentItemData({ _addItemsSectionKey: sectionKey });
    setCurrentQuestion(countQuestion);
    setAnsweredQuestions([]);
    setNavigationHistory([]);
  };

  // Delete handler for looped items (ponds, walls, curb, ADA ramps)
  const handleDeleteItem = async (sectionKey, itemId, itemName) => {
    // Close the delete dialog
    setDeleteDialog({
      isOpen: false,
      itemId: null,
      itemName: "",
      itemType: "",
      sectionKey: "",
      cascadeInfo: null,
    });

    try {
      // Delete using unified JSONB storage
      const result = await ModelPlannerService.deleteSectionData(session.id, itemId);

      if (result.error) {
        alert(`Failed to delete: ${result.error.message}`);
        return;
      }

      // Refresh session data
      await refreshSessionData();

      // Get updated items after deletion
      const dataKey = getSectionDataKey(sectionKey);
      const updatedItems = sessionData[dataKey] || [];

      if (updatedItems.length === 0) {
        // No items left - go back to initial question
        const initialQ = wizardEngine.getInitialQuestion(sectionKey);
        setCurrentQuestion(initialQ);
        setLoopState(null);
        setCurrentItemData({});
        setAnsweredQuestions([]);
        setNavigationHistory([]);
      } else if (currentQuestion?.type === "confirmation") {
        // If on confirmation page and items remain, show summary
        handleSectionSummaryClick(sectionKey);
      }
      // If on summary view with items remaining, it will auto-refresh from sessionData update
    } catch (error) {
      console.error("Delete error:", error);
      alert("An unexpected error occurred. Please try again.");
    }
  };

  // Clear handler for single sections (grading, ditches, sidewalks, building-pads, grate-elevations)
  const handleClearSection = async (sectionKey) => {
    // Close the delete dialog
    setDeleteDialog({
      isOpen: false,
      itemId: null,
      itemName: "",
      itemType: "",
      sectionKey: "",
      cascadeInfo: null,
    });

    try {
      // Check for cascade delete (sidewalks → ADA ramps)
      if (sectionKey === "sidewalks") {
        const adaRamps = sessionData.adaRamps || [];
        if (adaRamps.length > 0) {
          // Delete all ADA ramps first using unified JSONB storage
          await ModelPlannerService.deleteAllSectionData(session.id, "ada-ramps");
        }
      }

      // Clear the section using unified JSONB storage
      const result = await ModelPlannerService.deleteAllSectionData(
        session.id,
        sectionKey,
      );

      if (result.error) {
        alert(`Failed to clear section: ${result.error.message}`);
        return;
      }

      // Refresh session data
      await refreshSessionData();

      // Navigate to section's initial question after clearing
      const initialQ = wizardEngine.getInitialQuestion(sectionKey);
      setCurrentQuestion(initialQ);
      setLoopState(null);
      setCurrentItemData({});
      setAnsweredQuestions([]);
      setNavigationHistory([]);
    } catch (error) {
      console.error("Clear section error:", error);
      alert("An unexpected error occurred. Please try again.");
    }
  };

  // Show delete confirmation dialog
  const showDeleteConfirmation = (sectionKey, itemId, itemName, itemType) => {
    // Check for cascade info
    let cascadeInfo = null;
    if (sectionKey === "sidewalks" && itemType === "section") {
      const adaRamps = sessionData.adaRamps || [];
      if (adaRamps.length > 0) {
        cascadeInfo = {
          count: adaRamps.length,
          itemType: adaRamps.length === 1 ? "ADA ramp" : "ADA ramps",
        };
      }
    }

    setDeleteDialog({
      isOpen: true,
      itemId,
      itemName,
      itemType,
      sectionKey,
      cascadeInfo,
    });
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.itemType === "section") {
      handleClearSection(deleteDialog.sectionKey);
    } else {
      handleDeleteItem(
        deleteDialog.sectionKey,
        deleteDialog.itemId,
        deleteDialog.itemName,
      );
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialog({
      isOpen: false,
      itemId: null,
      itemName: "",
      itemType: "",
      sectionKey: "",
      cascadeInfo: null,
    });
  };

  const handleIncompleteWarning = (section) => {
    // Allow navigation without warning - progress is auto-saved
    return true;
  };

  const handleCreateDiscrepancy = () => {
    if (onOpenDiscrepancyModal) {
      onOpenDiscrepancyModal(project);
    }
  };

  const handleClose = () => {
    // Clean up URL params when closing modal
    const params = new URLSearchParams(window.location.search);
    params.delete("section");
    params.delete("question");

    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    window.history.pushState({}, "", newUrl);

    // Auto-save ensures all progress is saved
    onClose();
  };

  // Check if at least one section is completed for export button
  const isExportEnabled = () => {
    if (!sessionData) return false;

    // Check looped sections
    const hasCompletedPonds =
      sessionData.ponds &&
      sessionData.ponds.length > 0 &&
      sessionData.ponds.some((p) => p.completed === true);
    const hasCompletedCurbs =
      sessionData.curbTypes &&
      sessionData.curbTypes.length > 0 &&
      sessionData.curbTypes.some((c) => c.completed === true);
    const hasCompletedWalls =
      sessionData.walls &&
      sessionData.walls.length > 0 &&
      sessionData.walls.some((w) => w.completed === true);

    // Check single sections
    const hasCompletedGrading = sessionData.gradingSections?.completed === true;
    const hasCompletedDitches = sessionData.ditchesSwales?.completed === true;
    const hasCompletedSidewalks = sessionData.sidewalks?.completed === true;
    const hasCompletedBuildingPads =
      sessionData.buildingPads?.completed === true;
    const hasCompletedGrateElevations =
      sessionData.grateElevations?.completed === true;

    return (
      hasCompletedPonds ||
      hasCompletedCurbs ||
      hasCompletedWalls ||
      hasCompletedGrading ||
      hasCompletedDitches ||
      hasCompletedSidewalks ||
      hasCompletedBuildingPads ||
      hasCompletedGrateElevations
    );
  };

  // Handle export
  const handleExport = async (notes) => {
    try {
      await modelPlannerExportService.generatePDF(sessionData, project, notes);
    } catch (error) {
      console.error("Export error:", error);
      throw error; // Re-throw to let ExportButton handle the error display
    }
  };

  if (!isOpen) return null;

  return (
    <div className="model-planner-modal-overlay">
      <div className="model-planner-modal-content">
        {/* Project-Specific Modal Header - Matches standardization guide */}
        <div
          style={{
            padding: "var(--space-6)",
            borderBottom: "1px solid var(--gray-700)",
            background:
              "linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))",
            borderTopLeftRadius: "var(--radius-lg)",
            borderTopRightRadius: "var(--radius-lg)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-4)",
            }}
          >
            {/* Job Number and Project Name on one line - Standard for project modals */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "var(--space-3)",
                }}
              >
                <span
                  style={{
                    fontSize: "var(--text-2xl)",
                    fontWeight: "var(--font-semibold)",
                    color: "var(--gray-400)",
                    flexShrink: 0,
                  }}
                >
                  {formatJobRevNumber(project?.jobNumber, project?.rev)}
                </span>
                <h1
                  style={{
                    margin: 0,
                    fontSize: "var(--text-2xl)",
                    fontWeight: "var(--font-semibold)",
                    color: "var(--gray-50)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {project?.projectName || project?.name || "Model Planner"}
                </h1>
              </div>
              <p
                style={{
                  margin: "var(--space-2) 0 0 0",
                  fontSize: "var(--text-sm)",
                  color: "var(--gray-400)",
                }}
              >
                Model Planner Wizard
              </p>
            </div>

            {/* Export Button */}
            {!loading && (
              <ExportButton
                sessionData={sessionData}
                project={project}
                disabled={!isExportEnabled()}
                onExport={handleExport}
              />
            )}

            {/* Close Button */}
            <button
              onClick={handleClose}
              aria-label="Close modal"
              style={{
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: "none",
                borderRadius: "var(--radius-md)",
                color: "var(--gray-400)",
                cursor: "pointer",
                transition: "all 0.2s ease",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "var(--gray-700)";
                e.target.style.color = "var(--gray-200)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "transparent";
                e.target.style.color = "var(--gray-400)";
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div
          style={{
            flex: 1,
            display: "flex",
            overflow: "hidden",
            background: "var(--dark-bg)",
          }}
        >
          {/* Left Navigation */}
          {!loading && wizardEngine && (
            <WizardNavigation
              sections={wizardEngine.sections}
              currentSection={currentSection}
              currentItemIndex={loopState?.currentIndex}
              currentQuestionId={currentQuestion?.id}
              sessionData={sessionData}
              onSectionSelect={handleSectionSelect}
              onIncompleteWarning={handleIncompleteWarning}
              onItemClick={handleItemClick}
              onSectionSummaryClick={handleSectionSummaryClick}
              wizardEngine={wizardEngine}
            />
          )}

          {/* Main Question Area */}
          <div
            ref={questionAreaRef}
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "var(--space-6)",
              background: "var(--dark-bg)",
            }}
          >
            {loading && (
              <div
                style={{
                  textAlign: "center",
                  padding: "var(--space-12)",
                  color: "var(--gray-400)",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    width: "32px",
                    height: "32px",
                    border: "3px solid var(--gray-600)",
                    borderTopColor: "var(--cta-blue)",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
                <p
                  style={{
                    marginTop: "var(--space-4)",
                    fontSize: "var(--text-sm)",
                    color: "var(--gray-400)",
                  }}
                >
                  Loading wizard...
                </p>
              </div>
            )}

            {!loading && loopState && (
              <div
                style={{
                  marginBottom: "var(--space-5)",
                  padding: "var(--space-3)",
                  background: "rgba(59, 130, 246, 0.1)",
                  border: "1px solid rgba(59, 130, 246, 0.3)",
                  borderRadius: "var(--radius-md)",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-semibold)",
                    color: "var(--cta-blue)",
                  }}
                >
                  {getSectionItemLabel(currentSection)}{" "}
                  {loopState.currentIndex + 1} of {loopState.totalCount}
                </p>
              </div>
            )}

            {/* Hide answered questions - only show current question */}

            {/* Render current question */}
            {!loading && currentQuestion && (
              <WizardQuestion
                key={`${currentSection}-${currentQuestion.id}-${loopState?.currentIndex || 0}`}
                question={currentQuestion}
                currentData={currentItemData}
                onAnswer={handleAnswer}
                onBack={handleBack}
                canGoBack={navigationHistory.length > 0}
                projectId={project?.id}
                clientId={project?.clientId || project?.client_id}
                onCreateDiscrepancy={handleCreateDiscrepancy}
                sessionData={sessionData}
                onItemClick={handleItemClick}
                currentSection={currentSection}
                onEditSingleQuestion={handleEditFromConfirmation}
                onAddAnotherItem={handleAddAnotherItem}
                onDeleteItem={showDeleteConfirmation}
              />
            )}
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          isOpen={deleteDialog.isOpen}
          itemName={deleteDialog.itemName}
          itemType={deleteDialog.itemType}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          cascadeInfo={deleteDialog.cascadeInfo}
        />
      </div>
    </div>
  );
};

export default ModelPlannerWizardModal;
